<template>
  <Dropdown
    :class="$style.filterValueDropdown"
    :data-test-id="$testId('filterValueDropdown')"
    :isMenuOpen="isMenuOpen"
    @clickaway="handleFilterClose"
  >
    <template v-slot:button>
      <button
        :class="$style.filterButton"
        :data-test-id="$testId('filterValueButton')"
        @click="handleFilterClick"
      >
        {{ buttonText }}
        <Icon
          :class="$style.buttonCaret"
          icon="caretDown"
          :inline="true"
          :scale="0.5"
        />
        <CloseIcon
          v-if="showDelete"
          :class="$style.buttonDelete"
          :data-test-id="$testId('filterDeleteButton')"
          @click.stop="handleDeleteFilter"
        />
      </button>
    </template>
    <template v-slot:menu>
      <div
        :class="$style.dropdownContent"
      >
        <Listbox
          v-if="!loading"
          :data-test-id="$testId('filterValueList')"
          :options="getFilterOptions"
          :showSearch="true"
          showSelectAll
          :value="getFilterValues"
          @input="handleFilterChange"
          @selectAll="toggleSelectAll"
        />
        <LoadingIcon
          v-else
          :data-test-id="$testId('loadingIndicator')"
          :isCloud="false"
          :scale="2"
        />
      </div>
    </template>
  </Dropdown>
</template>

<script>
import {
  Dropdown,
  Icon,
  Listbox,
  LoadingIcon,
  Types,
} from '@jumpcloud/ui-components';
import { cloudInsightsColumns } from 'client/CloudInsights/constants/CloudInsightsConstants';
import { get } from 'lodash';
import {
  mapActions,
  mapGetters,
  mapState,
} from 'vuex';
import { notify } from 'client/util';
import CloseIcon from 'public/img/icons/close.svg?inline';

const { ListboxItem } = Types;

const cloudInsightsEventsModule = 'CloudInsightsEventsCollection';

export default {
  name: 'CloudInsightsFilter',

  components: {
    CloseIcon,
    Dropdown,
    Icon,
    Listbox,
    LoadingIcon,
  },

  props: {
    filter: {
      required: true,
      type: Object,
    },
    showDelete: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      isMenuOpen: false,
      loading: false,
    };
  },

  computed: {
    ...mapGetters(cloudInsightsEventsModule, [
      'getFilters',
    ]),
    ...mapState(cloudInsightsEventsModule, [
      'filterOptions',
    ]),

    buttonText() {
      const filterValues = this.getFilterValues;

      if (!filterValues.length) return `${this.filter.name}: all`;

      if (filterValues.length === 1) {
        const valueText = filterValues[0].value;
        if (valueText.length > 15) {
          return `${this.filter.name}: ${valueText.substring(0, 15)}...`;
        }
        return `${this.filter.name}: ${valueText}`;
      }

      return `${this.filter.name}: ${filterValues.length} values`;
    },

    filterValues() {
      const filter = this.getFilters.find((f) => (f.field === this.filter.value));
      if (filter) {
        return filter.values;
      }
      return [];
    },

    getFilterOptions() {
      const values = get(this.filterOptions, this.filter.value, []);

      const allOptions = this.filterValues.length
        ? [...new Set([...this.filterValues, ...values])]
        : [...values];

      if (this.filter.value === cloudInsightsColumns.event.userIdentity.principalId) {
        const filterMap = this.getPrincipalIdFilterMap(allOptions);
        // Reformat options to work with listbox
        return Object.keys(filterMap).sort()
          .map((value) => new ListboxItem({ name: value, value }));
      }

      const sortedOptions = allOptions.sort();
      // Reformat options to work with listbox
      return sortedOptions.map(filter => new ListboxItem({ name: filter, value: filter }));
    },

    getFilterValues() {
      // Listbox component uses `Array.prototype.includes`, which uses strict equality to evaluate
      // if all options are selected. Need to use the same object references
      // for value and options props to correctly show 'Select All' as checked / indeterminate.
      const getOptionRef = (value) => this.getFilterOptions.find(option => option.value === value);

      if (this.filter.value === cloudInsightsColumns.event.userIdentity.principalId) {
        const filterMap = this.getPrincipalIdFilterMap(this.filterValues);
        return Object.keys(filterMap).map(getOptionRef);
      }

      return this.filterValues.map(getOptionRef);
    },
  },

  created() {
    this.loadInitialData();
  },

  methods: {
    ...mapActions(cloudInsightsEventsModule, [
      'fetchDistinctValues',
    ]),

    allFiltersSelected(newFilters) {
      return this.getFilterOptions.every((option) => newFilters.some(
        selected => selected.value === option.value,
      ));
    },

    getPrincipalIdFilterMap(filterValuesArray) {
      // Create object with display value as key and full strings as values
      // Example: { autoscaling: 'aroa3uwu5nobzikpd5np5:autoscaling' }
      const filterMap = filterValuesArray.reduce((acc, value) => {
        const displayPrincipalId = value.split(':').at(-1) || value;
        if (!acc[displayPrincipalId]) {
          acc[displayPrincipalId] = [];
        }
        acc[displayPrincipalId].push(value);
        return acc;
      }, {});
      return filterMap;
    },

    handleDeleteFilter() {
      this.$emit('delete', this.filter);
    },

    handleFilterChange(newFilters) {
      let values = [];
      newFilters.forEach(newFilter => {
        if (this.filter.value === cloudInsightsColumns.event.userIdentity.principalId) {
          // Get all filter values that match newly selected filter value (aws)
          // and return array of full strings
          const options = get(this.filterOptions, this.filter.value, []);

          const allOptions = this.filterValues.length
            ? [...new Set([...this.filterValues, ...options])]
            : [...options];

          const filterMap = this.getPrincipalIdFilterMap(allOptions);

          values = [
            ...values,
            ...filterMap[newFilter.value] || [],
          ];
        } else {
          values.push(newFilter.value);
        }
      });
      this.$emit('updateFilters', {
        field: this.filter.value,
        values,
      });
    },

    handleFilterClick() {
      if (!this.isMenuOpen) {
        this.loadInitialData();
      }

      this.isMenuOpen = !this.isMenuOpen;
    },

    handleFilterClose() {
      // If closing without save, reset filters
      this.isMenuOpen = false;
    },

    async loadInitialData() {
      this.loading = true;
      try {
        await this.fetchDistinctValues({ field: this.filter.value });
      } catch (e) {
        notify('error', 'Unable to fetch distinct values');
      } finally {
        this.loading = false;
      }
    },

    toggleSelectAll() {
      const filterOptions = this.allFiltersSelected(this.getFilterValues)
        ? []
        : this.getFilterOptions;
      this.handleFilterChange(filterOptions);
    },
  },
};
</script>

<style scoped module>
.buttonCaret {
  height: var(--jc-spacer-small);
  margin-left: 6px;
  opacity: 0.6;
  position: relative;
}

.buttonDelete {
  border: 1px solid var(--jcBlack);
  border-radius: 50%;
  height: 0.875rem;
  margin-left: 10px;
  opacity: 0.6;
}

.buttonDelete:focus { outline: none; }
.buttonDelete:hover { background: var(--jcWhite50); }

.dropDownButton:hover .closeIcon {
  background-color: var(--jcWhite50);
  fill: var(--jcGrey);
}

.dropdownContent {
  background: var(--jcWhite50);
  border: 1px solid var(--jc-border-color-medium);
  border-radius: var(--jc-border-radius);
  left: 0;
  margin: var(--jc-spacer-large) 0;
  padding: var(--jc-spacer);
  position: absolute;
  top: var(--jc-spacer-small);
  width: 300px;
  word-break: break-all;
  z-index: 6;
}

.filterButton {
  align-items: center;
  background-color: transparent;
  border: var(--jc-border-width) solid var(--jc-border-color-medium);
  border-radius: var(--jc-border-radius-small);
  cursor: pointer;
  display: flex;
  justify-content: center;
  line-height: 1;
  margin-right: var(--jc-spacer-small);
  min-width: var(--jc-button-min-width);
  padding: var(--jc-spacer-small) var(--jc-spacer-small);
}

.filterButton:hover {
  background-color: var(--jcGrey200);
}

.filterValueDropdown {
  z-index: auto;
}
</style>
